// src/pages/ResourceView.js
import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Typography, Button, Paper, Stack, Chip } from "@mui/material";
import {
  getFirestore, collection, query, where, limit, getDocs
} from "firebase/firestore";

import { Helmet } from "react-helmet-async";


const formatDate = (v) => {
  if (!v) return "";
  const d = typeof v?.toDate === "function" ? v.toDate() : new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatAuthor = (email) => {
  if (!email) return "WorkPulse.in";
  if (email.toLowerCase() === "khandhadiahitesh@gmail.com") return "Hitesh Khandhadia (FCA, DISA, B.Com , AICA Level-1) ";
  return email;
};



export default function ResourceView() {



  const { slug } = useParams();
  const db = getFirestore();

  const [docData, setDocData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const q = query(
        collection(db, "resources"),
        where("slug", "==", slug),
        where("status", "==", "published"),
        limit(1)
      );
      const snap = await getDocs(q);
      if (snap.empty) {
        setDocData(null);
      } else {
        setDocData({ id: snap.docs[0].id, ...snap.docs[0].data() });
      }
      setLoading(false);
    })();
  }, [db, slug]);

  if (loading) return <Typography sx={{ p: 2 }}>Loading…</Typography>;
  if (!docData) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">Article not found.</Typography>
        <Button component={RouterLink} to="/resources" sx={{ mt: 2 }}>
          Back to Resources
        </Button>
      </Box>
    );
  }

  const pageTitle = docData?.title
    ? `${docData.title} | WorkPulse.in Resources`
    : "WorkPulse.in Resources";

  const pageDesc =
    (docData?.excerpt || "")
      .replace(/<[^>]*>/g, "")
      .trim()
      .slice(0, 155) ||
    "Practical articles and templates for CA firms using WorkPulse.in.";

  const canonicalUrl = `https://workpulse.in/resources/${slug}`;

  const publishedISO =
    docData?.publishedAt?.toDate
      ? docData.publishedAt.toDate().toISOString()
      : docData?.publishedAt?.seconds
        ? new Date(docData.publishedAt.seconds * 1000).toISOString()
        : null;

  const schemaArticle = docData?.title
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: docData.title,
        description: pageDesc,
        author: {
          "@type": "Person",
          name: formatAuthor(docData.authorEmail) || "WorkPulse.in",
        },
        publisher: {
          "@type": "Organization",
          name: "WorkPulse.in",
          logo: {
            "@type": "ImageObject",
            url: "https://workpulse.in/logo512.png",
          },
        },
        mainEntityOfPage: canonicalUrl,
        datePublished: publishedISO || undefined,
        dateModified: publishedISO || undefined,
      }
    : null;


  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3 } }}>
      <Button component={RouterLink} to="/resources" sx={{ mb: 2 }}>
        ← Back
      </Button>

      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />

        {schemaArticle && (
          <script type="application/ld+json">
            {JSON.stringify(schemaArticle)}
          </script>
        )}
      </Helmet>


      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          {docData.title}
        </Typography>


        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {!!docData.publishedAt ? `Published: ${formatDate(docData.publishedAt)} • ` : ""}
        {`Author: ${formatAuthor(docData.authorEmail)}`}
        </Typography>



        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
        {!!docData.category && (
            <Chip label={docData.category} size="small" variant="outlined" />
        )}

        {(docData.tags || []).map(tag => (
            <Chip key={tag} label={tag} size="small" />
        ))}
        </Stack>


        {!!docData.excerpt && (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            {docData.excerpt}
          </Typography>
        )}

        {/* Rendering HTML content */}
        <Box sx={{ mt: 3 }}>
          <div
            dangerouslySetInnerHTML={{ __html: docData.content || "" }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
